import { useState } from "react";
import { WeaponBlock, WeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Button, Chip, FormGroup, Stack, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import StatLookupField from "../../../components/StatLookupField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import { ModalContentSection } from "../../../components/Modal";
import SelectField from "../../../components/formFields/SelectField";
import ButtonFlat from "../../../components/ButtonFlat";

import { CheckboxField } from "../../../components/formFields/CheckboxField";
import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import AbilityEditor from "../../characterPage/AbilityEditor";
import Page from "../../../components/Page";
import { CreateDefaultCombatSkills } from "../../../master/data";
import { StatDisplay } from "../../../components/StatDisplay";
import TagList from "../../../components/TagList";

type Block = WeaponBlock | WeaponBlockBase;

const defaultBlock: WeaponBlock = {
    id: "",
    name: "Weapon ",
    slots: 0,
    acquire: 0,
    critDamage: 0,
    enduranceDamage: 0,
    hitDice: 0,
    hitBonus: 0,
    range: 0,
    resources: 0,
    knowledge: 0,
    influence: 0,
    weaponType: [],
    addFeatures: [],
    removeFeatures: [],
    strongHits: [],
    passives: [],
    draw: 0,
    reload: 0,
    strongHitRange: 0,
    hands: 1,
    size: "standard",
    notes: "",
    sidearmDiscount: false,
}

const baseBlock: WeaponBlockBase = {
    id: "",
    name: "Base Weapon",
    slots: 0,
    acquire: 0,
    critDamage: 0,
    enduranceDamage: 0,
    hitDice: 0,
    hitBonus: 0,
    range: 0,
    resources: 0,
    knowledge: 0,
    influence: 0,
    weaponType: [],
    addFeatures: [],
    removeFeatures: [],
    passives: [],
    strongHits: [],
    skillBonusId: "",
    draw: 1,
    reload: 2,
    strongHitRange: 0,
    size: "standard",
    hands: 2,
    notes: "",
    sidearmDiscount: false,
}

export const GetNew = (name: string, isModifier: boolean, isBase: boolean): WeaponBlock => {
    return {
        ...(isBase ? baseBlock : defaultBlock),
        id: v4(),
        name: name
    }
}

export interface WeaponEditorProps {
    prefix: string;
    pathType: string;
    defaultName: string;
    isModifier?: boolean;
    isBase?: boolean;
}

export const WeaponBlockEditor = (props: {
        weaponBlock: WeaponBlock | WeaponBlockBase,
        setWeaponBlock: (weaponBlock: WeaponBlock | WeaponBlockBase) => void,
        showSidearmOption?: boolean,
        isModifier?: boolean,
        isBase?: boolean,
    }        
    ) => {
    const { weaponBlock: current, setWeaponBlock, isModifier } = props;
    const modifier = isModifier == true;

    const onChange = (key: string, value: any) => {
        setWeaponBlock({
            ...current,
            [key]: value
        })
    }

    const handleAddFeature = (value: string) => {
        const newFeatures = [ ...current.addFeatures, value ];
        onChange("addFeatures", newFeatures);
    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...current.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    const handleAddType = (value: string) => {
        const list = [ ...current.weaponType, value ];
        onChange("weaponType", list);
    }

    const handleRemoveType = (index: number) => {
        const list = [ ...current.weaponType ];
        list.splice(index, 1);
        onChange("weaponType", list);
    }

    const strongHitRangeLabel = (value: number) => {
        if((value ?? 0) < 0) return "-";
        return `${6-(value ?? 0)}-6`;
    }

    return <>
        <Stack spacing={1}>
                <FormFieldLabel label="Name">
                    <TextField fullWidth type="text" variant="standard" value={current.name}
                        onChange={(event) => onChange("name",event.currentTarget.value)} />
                </FormFieldLabel>
                <FormFieldLabel label="Hit Dice" justifyContent="end">
                    <NumberField step={1} min={0} value={current.hitDice}
                        onChange={(value) => onChange("hitDice", value)} customRenderer={(val) => `${val}d6`}/>
                </FormFieldLabel>
                <FormFieldLabel label="Hit Bonus" justifyContent="end">
                    <NumberField step={1} value={current.hitBonus}
                        onChange={(value) => onChange("hitBonus", value)} customRenderer={(v) => StatDisplay(v)} />
                </FormFieldLabel>
                {props.isBase &&
                <FormFieldLabel label="Skill" justifyContent="end"><StatLookupField onChange={(value) => onChange("skillBonusId", value)}
                    // @ts-ignore
                    value={current.skillBonusId}
                    stats={[ ...CreateDefaultCombatSkills(), ]} /></FormFieldLabel> }
                <FormFieldLabel label="Range" justifyContent="end">
                    <NumberField step={1} value={current.range} onChange={(value) => onChange("range", value)} customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Endurance Damage" justifyContent="end">
                    <NumberField step={1} value={current.enduranceDamage} onChange={(value) => onChange("enduranceDamage", value)}
                         customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Crit Damage" justifyContent="end">
                    <NumberField step={1} value={current.critDamage} onChange={(value) => onChange("critDamage", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label={`Strong Hit Range (${strongHitRangeLabel(current.strongHitRange)})`} justifyContent="end">
                    <NumberField step={1} value={current.strongHitRange} onChange={(value) => onChange("strongHitRange", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                {props.isBase &&
                <SelectField value={current.size} onChange={(value) => onChange("size", value)} options={[
                    { name: `Size - None (0)`, value: "none" },
                    { name: `Size - Trinket (0.25)`, value: "trinket" },
                    { name: `Size - Small (1)`, value: "small" } ,
                    { name: `Size - Standard (2)`, value: "standard" } ,
                    { name: `Size - Large (3)`, value: "large" } ,
                ]} />}
                <FormFieldLabel label="Equipment Slots" justifyContent="end">
                    <NumberField step={1} value={current.slots} onChange={(value) => onChange("slots", value)}
                        customRenderer={(v) => StatDisplay(v)} />
                </FormFieldLabel>
                
                <FormFieldLabel label="Hands" justifyContent="end">
                    <NumberField step={1} value={current.hands} onChange={(value) => onChange("hands", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Draw" justifyContent="end">
                    <NumberField step={1} value={current.draw} onChange={(value) => onChange("draw", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Reload" justifyContent="end">
                    <NumberField step={1} value={current.reload} onChange={(value) => onChange("reload", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Acquire" justifyContent="end">
                    <NumberField step={1} value={current.acquire} onChange={(value) => onChange("acquire", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v, "t") : `${v}t`} />
                </FormFieldLabel>
                <FormFieldLabel label="Influence" justifyContent="end">
                    <NumberField step={1} value={current.influence} onChange={(value) => onChange("influence", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                <FormFieldLabel label="Resources" justifyContent="end">
                    <NumberField step={1} value={current.resources} onChange={(value) => onChange("resources", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v) : v} />
                </FormFieldLabel>
                {true && <FormFieldLabel label="Apply Sidearm Discount (-1 Resource)" justifyContent="end">
                    <CheckboxField value={current.sidearmDiscount} onChange={(value) => onChange("sidearmDiscount", value)} />
                </FormFieldLabel>}
                <FormFieldLabel label="Knowledge" justifyContent="end">
                    <NumberField step={1} value={current.knowledge} onChange={(value) => onChange("knowledge", value)}
                        customRenderer={(v) => modifier ? StatDisplay(v, "kn") : `${v}kn`} />
                </FormFieldLabel>
            </Stack>
            <ModalContentSection title="Weapon Type">
                <TagList items={current.weaponType} onAdd={handleAddType} onRemove={handleRemoveType} />
            </ModalContentSection>
            <ModalContentSection title="Keywords">
                <TagList items={current.addFeatures} onAdd={handleAddFeature} onRemove={handleRemoveFeature} />
            </ModalContentSection>
            <AbilityEditor title="Strong Hit Options" abilities={current.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
            <AbilityEditor title="Passive Abilities" abilities={current.passives} setAbilities={(value) => onChange("passives", value)} />
            <Box paddingLeft={1}>
                <Typography>Notes</Typography>
                <TextField value={current.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
            </Box>
    </>
}

export const WeaponBlockEditorPage = (props: WeaponEditorProps) => {
    const { prefix, pathType, defaultName } = props;
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<Block>(loadLibraryData<Block>(prefix, id ?? "NONE") ?? GetNew(defaultName, props.isModifier ?? false, props.isBase ?? false));

    const handleSave = () => {
        navigate(`/library/${pathType}/${current.id}`, {replace: true});
        saveLibraryData(prefix, current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, prefix);
    }

    return <Page>
        <h2>{current.name}</h2>
        <FormGroup>
            <WeaponBlockEditor setWeaponBlock={setCurrent} weaponBlock={current} isBase={props.isBase} isModifier={props.isModifier} />
            <Stack direction={"row"} spacing={1} paddingTop={1}>
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
            </FormGroup>
        </Page>
}